import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { db, auth } from "../firebase";
import { ref, get, update, remove, push, set, onValue, off } from "firebase/database";
import { updatePassword, reauthenticateWithCredential, EmailAuthProvider } from "firebase/auth";
import { TOTP, Secret } from "otpauth";
import { QRCodeSVG } from "qrcode.react";
import {
  ShieldCheck, CheckCircle, XCircle, Trash2, LogOut, Eye, EyeOff,
  Users, Package, Bell, Baby, Settings, ToggleLeft, ToggleRight, Save,
  Flag, BarChart2, TrendingUp, AlertTriangle, Send, Ban, RefreshCw,
  MessageSquare, DollarSign, Star, Download, Award, UserX, Key, Lock, Smartphone
} from "lucide-react";
import { DEFAULT_SETTINGS, saveAppSettings } from "../context/AppSettingsContext";

const FEATURE_LABELS = {
  listings:    { label: "Buy & Sell Listings",  desc: "Users can post and browse items for sale" },
  swap:        { label: "Swap Items",            desc: "Give one item, get one in return" },
  notes:       { label: "Study Notes",           desc: "Buy and sell study materials" },
  lostFound:   { label: "Lost & Found",          desc: "Report and find lost items" },
  rideshare:   { label: "Rideshare",             desc: "Share rides to/from campus" },
  events:      { label: "Events",                desc: "Post and discover campus events" },
  tutoring:    { label: "Tutoring",              desc: "Find and offer tutoring services" },
  requests:    { label: "Item Requests",         desc: "Request items you need" },
  giveaway:    { label: "Free Giveaways",        desc: "Give items for free" },
  noticeBoard: { label: "Notice Board",          desc: "Campus announcements" },
  parentZone:  { label: "Parent Zone",           desc: "School items for parents" },
};

const TAB_CONFIG = [
  { key: "verifications", label: "Verify IDs",   icon: ShieldCheck,   activeClass: "bg-emerald-600 text-white",   badgeClass: "bg-emerald-500" },
  { key: "listings",      label: "Listings",      icon: Package,       activeClass: "bg-indigo-600 text-white",    badgeClass: "bg-indigo-500" },
  { key: "users",         label: "Users",         icon: Users,         activeClass: "bg-blue-600 text-white",      badgeClass: "bg-blue-500" },
  { key: "parents",       label: "Parents",       icon: Baby,          activeClass: "bg-amber-500 text-white",     badgeClass: "bg-amber-400" },
  { key: "reports",       label: "Reports",       icon: Flag,          activeClass: "bg-red-600 text-white",       badgeClass: "bg-red-500" },
  { key: "analytics",    label: "Analytics",     icon: BarChart2,     activeClass: "bg-purple-600 text-white",    badgeClass: "bg-purple-500" },
  { key: "notices",       label: "Notices",       icon: Bell,          activeClass: "bg-pink-600 text-white",      badgeClass: "bg-pink-500" },
  { key: "features",      label: "Features",      icon: ToggleRight,   activeClass: "bg-teal-600 text-white",      badgeClass: "bg-teal-500" },
  { key: "settings",      label: "Settings",      icon: Settings,      activeClass: "bg-slate-600 text-white",     badgeClass: "bg-slate-500" },
  { key: "ambassadors",   label: "Ambassadors",   icon: Award,         activeClass: "bg-orange-600 text-white",    badgeClass: "bg-orange-500" },
  { key: "community",     label: "Community",     icon: MessageSquare, activeClass: "bg-cyan-600 text-white",      badgeClass: "bg-cyan-500" },
  { key: "feedback",      label: "Feedback",      icon: MessageSquare, activeClass: "bg-violet-600 text-white",    badgeClass: "bg-violet-500" },
];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("verifications");

  // Core data
  const [users, setUsers]               = useState([]);
  const [listings, setListings]         = useState([]);
  const [parentListings, setParentListings] = useState([]);
  const [reports, setReports]           = useState([]);
  const [loading, setLoading]           = useState(true);
  const [fetchError, setFetchError]     = useState("");

  // UI state
  const [selectedId, setSelectedId]     = useState(null);
  const [adminListingFilter, setAdminListingFilter] = useState("all");
  const [userSearch, setUserSearch]     = useState("");

  // Notice form
  const [noticeForm, setNoticeForm]     = useState({ title: "", content: "", category: "General" });
  const [postingNotice, setPostingNotice] = useState(false);
  const [pendingNotices, setPendingNotices] = useState([]);

  // Daily Deal
  const [dailyDealListingId, setDailyDealListingId] = useState("");
  const [savingDailyDeal, setSavingDailyDeal] = useState(false);

  // Broadcast
  const [broadcastMsg, setBroadcastMsg] = useState("");
  const [broadcasting, setBroadcasting] = useState(false);

  // Ambassador applications
  const [ambassadors, setAmbassadors] = useState([]);

  // Community posts
  const [communityPosts, setCommunityPosts] = useState([]);
  const [communityFilter, setCommunityFilter] = useState("all");
  const [loadingCommunity, setLoadingCommunity] = useState(false);

  // Feedback / complaints
  const [feedbackItems, setFeedbackItems] = useState([]);
  const [feedbackFilter, setFeedbackFilter] = useState("open");

  // Notify specific user
  const [notifyUserId, setNotifyUserId] = useState(null);
  const [notifyMsg, setNotifyMsg] = useState("");
  const [sendingNotify, setSendingNotify] = useState(false);

  // Settings
  const [appSettings, setAppSettings]   = useState(DEFAULT_SETTINGS);
  const [savingSettings, setSavingSettings] = useState(false);
  const [settingsSaved, setSettingsSaved]   = useState(false);

  // Stats banner edit
  const [statsForm, setStatsForm] = useState({ totalUsers: "", totalListings: "", totalColleges: "", visible: true });
  const [savingStats, setSavingStats] = useState(false);
  const [statsLoaded, setStatsLoaded] = useState(false);

  // Change password
  const [pwForm, setPwForm] = useState({ current: "", newPw: "", confirm: "" });
  const [pwError, setPwError] = useState("");
  const [pwSuccess, setPwSuccess] = useState("");
  const [changingPw, setChangingPw] = useState(false);
  const [showPw, setShowPw] = useState(false);
  // Change admin secret
  const [secretForm, setSecretForm] = useState({ current: "", newSecret: "", confirm: "" });
  const [secretError, setSecretError] = useState("");
  const [secretSuccess, setSecretSuccess] = useState("");
  const [changingSecret, setChangingSecret] = useState(false);

  // 2FA setup
  const [twoFAEnabled, setTwoFAEnabled] = useState(false);
  const [twoFASetupMode, setTwoFASetupMode] = useState(false);
  const [twoFATotp, setTwoFATotp] = useState(null); // TOTP object while setting up
  const [twoFAVerifyCode, setTwoFAVerifyCode] = useState("");
  const [twoFAError, setTwoFAError] = useState("");
  const [twoFASuccess, setTwoFASuccess] = useState("");

  // ─── Init ───────────────────────────────────────────────────────────────────

  useEffect(() => {
    const storedFlag = localStorage.getItem("cs_admin") === "true";
    const storedUid  = localStorage.getItem("cs_admin_uid");
    const firebaseUid = auth.currentUser?.uid;

    // Must have localStorage flag AND matching Firebase auth session AND UID in /admins
    if (!storedFlag || !firebaseUid || storedUid !== firebaseUid) {
      localStorage.removeItem("cs_admin");
      localStorage.removeItem("cs_admin_uid");
      navigate("/admin-login");
      return;
    }
    // Double-check UID is still in /admins node
    get(ref(db, `admins/${firebaseUid}`)).then(snap => {
      if (!snap.exists()) {
        localStorage.removeItem("cs_admin");
        localStorage.removeItem("cs_admin_uid");
        navigate("/admin-login");
      }
    });

    fetchSettings();
    fetchPendingNotices();
    fetchAmbassadors();
    fetchCommunityPosts();
    // Load current published stats
    get(ref(db, "appStats")).then(snap => {
      if (snap.exists()) {
        const s = snap.val();
        setStatsForm({
          totalUsers: s.totalUsers != null ? String(s.totalUsers) : "",
          totalListings: s.totalListings != null ? String(s.totalListings) : "",
          totalColleges: s.totalColleges != null ? String(s.totalColleges) : "",
          visible: s.visible !== false,
        });
      }
      setStatsLoaded(true);
    }).catch(() => setStatsLoaded(true));

    // Real-time listings listener
    const listingsRef2 = ref(db, "listings");
    onValue(listingsRef2, (snap) => {
      const allListings = snap.exists()
        ? Object.entries(snap.val()).map(([id, data]) => ({ id, ...data }))
        : [];
      setListings(allListings.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)));
      setLoading(false);
    });

    // Real-time users listener
    const usersRef2 = ref(db, "users");
    onValue(usersRef2, (snap) => {
      setUsers(snap.exists() ? Object.entries(snap.val()).map(([id, data]) => ({ id, ...data })) : []);
    });

    // Real-time reports listener
    const reportsRef2 = ref(db, "reports");
    onValue(reportsRef2, (snap) => {
      setReports(snap.exists() ? Object.entries(snap.val()).map(([id, data]) => ({ id, ...data })) : []);
    });

    // Real-time feedback listener
    const feedbackRef = ref(db, "feedback");
    const unsubFeedback = onValue(feedbackRef, (snap) => {
      if (snap.exists()) {
        let all = [];
        Object.entries(snap.val()).forEach(([uid, entries]) => {
          Object.entries(entries).forEach(([fid, data]) => {
            all.push({ id: fid, uid, ...data });
          });
        });
        all.sort((a, b) => b.createdAt - a.createdAt);
        setFeedbackItems(all);
      } else {
        setFeedbackItems([]);
      }
    });

    return () => off(feedbackRef);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function fetchPendingNotices() {
    try {
      const snap = await get(ref(db, "notices_pending"));
      if (snap.exists()) {
        const all = Object.entries(snap.val()).map(([id, data]) => ({ id, ...data }));
        setPendingNotices(all.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)));
      } else {
        setPendingNotices([]);
      }
    } catch (e) {  }
  }

  async function approveNotice(notice) {
    try {
      await push(ref(db, "notices"), {
        title: notice.title,
        content: notice.content,
        category: notice.category,
        pinned: false,
        postedBy: notice.postedBy,
        postedByName: notice.postedByName,
        college: notice.college,
        createdAt: notice.createdAt,
      });
      await remove(ref(db, `notices_pending/${notice.id}`));
      setPendingNotices(prev => prev.filter(n => n.id !== notice.id));
    } catch (e) { alert("Failed to approve."); }
  }

  async function rejectNotice(noticeId) {
    try {
      await remove(ref(db, `notices_pending/${noticeId}`));
      setPendingNotices(prev => prev.filter(n => n.id !== noticeId));
    } catch (e) { alert("Failed to reject."); }
  }

  // ─── Fetch helpers ──────────────────────────────────────────────────────────

  async function fetchSettings() {
    try {
      const [snap, totpSnap] = await Promise.all([
        get(ref(db, "appSettings")),
        get(ref(db, "adminConfig/totpSecret")),
      ]);
      if (snap.exists()) {
        const data = snap.val();
        setAppSettings({
          ...DEFAULT_SETTINGS,
          ...data,
          features: { ...DEFAULT_SETTINGS.features, ...(data.features || {}) },
        });
      }
      setTwoFAEnabled(totpSnap.exists());
    } catch (e) {  }
  }

  async function fetchAll() {
    setLoading(true);
    setFetchError("");
    try {
      const [uSnap, lSnap, pSnap, rSnap] = await Promise.all([
        get(ref(db, "users")),
        get(ref(db, "listings")),
        get(ref(db, "parent_listings")),
        get(ref(db, "reports")),
      ]);
      setUsers(uSnap.exists() ? Object.entries(uSnap.val()).map(([id, data]) => ({ id, ...data })) : []);
      const allListings = lSnap.exists()
        ? Object.entries(lSnap.val()).map(([id, data]) => ({ id, ...data }))
        : [];
      setListings(allListings.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)));
      const allParent = pSnap.exists()
        ? Object.entries(pSnap.val()).map(([id, data]) => ({ id, ...data }))
        : [];
      setParentListings(allParent.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)));
      setReports(rSnap.exists() ? Object.entries(rSnap.val()).map(([id, data]) => ({ id, ...data })) : []);
    } catch (e) {
      setFetchError("Failed to load data: " + (e?.message || e?.code || "Permission denied or network error"));
    }
    setLoading(false);
  }

  // ─── User actions ───────────────────────────────────────────────────────────

  async function approveUser(uid) {
    const updates = { [`users/${uid}/verified`]: true, [`users/${uid}/idVerificationStatus`]: "approved" };
    listings.filter(l => l.sellerId === uid).forEach(l => {
      updates[`listings/${l.id}/sellerVerified`] = true;
    });
    await update(ref(db), updates);
    setUsers(prev => prev.map(u => u.id === uid ? { ...u, verified: true, idVerificationStatus: "approved" } : u));
  }

  async function rejectUser(uid) {
    const updates = { [`users/${uid}/verified`]: false, [`users/${uid}/idVerificationStatus`]: "rejected" };
    listings.filter(l => l.sellerId === uid).forEach(l => {
      updates[`listings/${l.id}/sellerVerified`] = false;
    });
    await update(ref(db), updates);
    setUsers(prev => prev.map(u => u.id === uid ? { ...u, verified: false, idVerificationStatus: "rejected" } : u));
  }

  async function suspendUser(uid) {
    const user = users.find(u => u.id === uid);
    await Promise.all([
      update(ref(db, `users/${uid}`), { suspended: true }),
      set(ref(db, `bannedUsers/${uid}`), { reason: "admin_suspended", name: user?.name || "", suspendedAt: Date.now() }),
    ]);
    setUsers(prev => prev.map(u => u.id === uid ? { ...u, suspended: true } : u));
  }

  async function unsuspendUser(uid) {
    await Promise.all([
      update(ref(db, `users/${uid}`), { suspended: false }),
      remove(ref(db, `bannedUsers/${uid}`)),
    ]);
    setUsers(prev => prev.map(u => u.id === uid ? { ...u, suspended: false } : u));
  }

  // ─── Listing actions ────────────────────────────────────────────────────────

  async function toggleFeatureListing(id, currentFeatured) {
    await update(ref(db, `listings/${id}`), { featured: !currentFeatured });
    setListings(prev => prev.map(l => l.id === id ? { ...l, featured: !currentFeatured } : l));
  }

  async function saveDailyDeal() {
    if (!dailyDealListingId.trim()) { alert("Enter a listing ID."); return; }
    setSavingDailyDeal(true);
    try {
      await set(ref(db, "dailyDeal/current"), { listingId: dailyDealListingId.trim(), setAt: Date.now() });
      alert("Daily Deal set!");
      setDailyDealListingId("");
    } catch (e) { alert("Failed to set daily deal."); }
    setSavingDailyDeal(false);
  }

  async function clearDailyDeal() {
    await set(ref(db, "dailyDeal/current"), null);
    alert("Daily Deal cleared.");
  }

  async function deleteListing(id) {
    if (!window.confirm("Delete this listing?")) return;
    const listing = listings.find(l => l.id === id);
    await remove(ref(db, `listings/${id}`));
    if (listing?.sellerId) {
      await push(ref(db, `notifications/${listing.sellerId}`), {
        type: "listing_removed",
        message: `Your listing "${listing.title}" was removed by admin for violating community guidelines. Contact support if you think this is a mistake.`,
        createdAt: Date.now(),
        read: false,
      });
    }
    setListings(prev => prev.filter(l => l.id !== id));
  }

  async function relistListing(id) {
    await update(ref(db, `listings/${id}`), { status: "active", reservedBy: null, reservedUntil: null });
    setListings(prev => prev.map(l => l.id === id ? { ...l, status: "active", reservedBy: null, reservedUntil: null } : l));
  }

  async function deleteParentListing(id) {
    if (!window.confirm("Delete this parent listing?")) return;
    await remove(ref(db, `parent_listings/${id}`));
    setParentListings(prev => prev.filter(l => l.id !== id));
  }

  // ─── Report actions ─────────────────────────────────────────────────────────

  async function deleteListingFromReport(reportId, listingId) {
    if (!window.confirm("Delete this listing and dismiss the report?")) return;
    const listing = listings.find(l => l.id === listingId);
    await remove(ref(db, `listings/${listingId}`));
    await remove(ref(db, `reports/${reportId}`));
    if (listing?.sellerId) {
      await push(ref(db, `notifications/${listing.sellerId}`), {
        type: "listing_removed",
        message: `Your listing "${listing.title}" was removed by admin after a report. Contact support if you think this is a mistake.`,
        createdAt: Date.now(),
        read: false,
      });
    }
    setListings(prev => prev.filter(l => l.id !== listingId));
    setReports(prev => prev.filter(r => r.id !== reportId));
  }

  async function dismissReport(reportId) {
    await remove(ref(db, `reports/${reportId}`));
    setReports(prev => prev.filter(r => r.id !== reportId));
  }

  // ─── Notice / Broadcast ─────────────────────────────────────────────────────

  async function postNotice(e) {
    e.preventDefault();
    setPostingNotice(true);
    try {
      await push(ref(db, "notices"), {
        ...noticeForm,
        pinned: true,
        postedBy: "admin",
        postedByName: "CampusSwap Admin",
        college: "All Colleges",
        createdAt: new Date().toISOString(),
      });
      setNoticeForm({ title: "", content: "", category: "General" });
      alert("Notice posted!");
    } catch (e) { alert("Failed to post."); }
    setPostingNotice(false);
  }

  async function broadcastToAllUsers() {
    if (!broadcastMsg.trim()) { alert("Enter a message first."); return; }
    if (!window.confirm(`Send broadcast to all ${realUsers.length} users?`)) return;
    setBroadcasting(true);
    try {
      const writes = realUsers.map(u =>
        push(ref(db, `notifications/${u.id}`), {
          type: "broadcast",
          message: broadcastMsg.trim(),
          createdAt: Date.now(),
          read: false,
          from: "CampusSwap Admin",
        })
      );
      await Promise.all(writes);
      setBroadcastMsg("");
      alert("Broadcast sent to all users!");
    } catch (err) { alert("Broadcast failed: " + err.message); }
    setBroadcasting(false);
  }

  // ─── Settings ───────────────────────────────────────────────────────────────

  async function handleSaveSettings() {
    setSavingSettings(true);
    try {
      await saveAppSettings(appSettings);
      setSettingsSaved(true);
      setTimeout(() => setSettingsSaved(false), 3000);
    } catch (e) { alert("Failed to save settings."); }
    setSavingSettings(false);
  }

  function toggleFeature(key) {
    setAppSettings(prev => ({
      ...prev,
      features: { ...prev.features, [key]: !prev.features[key] },
    }));
  }

  function handleLogout() {
    localStorage.removeItem("cs_admin");
    localStorage.removeItem("cs_admin_uid");
    navigate("/admin-login");
  }

  async function handleChangeFirebasePassword(e) {
    e.preventDefault();
    setPwError(""); setPwSuccess("");
    if (pwForm.newPw.length < 8) return setPwError("New password must be at least 8 characters.");
    if (pwForm.newPw !== pwForm.confirm) return setPwError("New passwords do not match.");
    setChangingPw(true);
    try {
      const user = auth.currentUser;
      const credential = EmailAuthProvider.credential(user.email, pwForm.current);
      await reauthenticateWithCredential(user, credential);
      await updatePassword(user, pwForm.newPw);
      setPwSuccess("Firebase account password changed successfully!");
      setPwForm({ current: "", newPw: "", confirm: "" });
    } catch (err) {
      if (err.code === "auth/wrong-password" || err.code === "auth/invalid-credential") {
        setPwError("Current password is incorrect.");
      } else {
        setPwError("Failed to change password. Try again.");
      }
    }
    setChangingPw(false);
  }

  async function handleChangeAdminSecret(e) {
    e.preventDefault();
    setSecretError(""); setSecretSuccess("");
    if (secretForm.newSecret.length < 8) return setSecretError("New secret must be at least 8 characters.");
    if (secretForm.newSecret !== secretForm.confirm) return setSecretError("Secrets do not match.");
    setChangingSecret(true);
    try {
      // Verify current secret first
      const snap = await get(ref(db, "adminConfig/secretPassword"));
      const currentSecret = snap.exists() ? snap.val() : null;
      if (!currentSecret) { setSecretError("Could not fetch current secret from server."); setChangingSecret(false); return; }
      if (secretForm.current !== currentSecret) {
        setSecretError("Current admin secret is incorrect.");
        setChangingSecret(false);
        return;
      }
      await set(ref(db, "adminConfig/secretPassword"), secretForm.newSecret);
      setSecretSuccess("Admin secret password updated!");
      setSecretForm({ current: "", newSecret: "", confirm: "" });
    } catch (err) {
      setSecretError("Failed to update. Try again.");
    }
    setChangingSecret(false);
  }

  // ─── New admin actions ────────────────────────────────────────────────────────

  async function deleteUser(uid, name) {
    if (!window.confirm(`Delete user "${name}"? Their profile and listings will be removed. They can re-register with the same email. Use Suspend to block them permanently.`)) return;
    await Promise.all([
      remove(ref(db, `users/${uid}`)),
      remove(ref(db, `bannedUsers/${uid}`)), // ensure not banned so they can re-register
    ]);
    setUsers(prev => prev.filter(u => u.id !== uid));
  }

  async function markListingAsSold(id) {
    await update(ref(db, `listings/${id}`), { status: "sold" });
    setListings(prev => prev.map(l => l.id === id ? { ...l, status: "sold" } : l));
  }

  async function sendNotificationToUser() {
    if (!notifyMsg.trim() || !notifyUserId) return;
    setSendingNotify(true);
    try {
      await push(ref(db, `notifications/${notifyUserId}`), {
        type: "admin",
        message: notifyMsg.trim(),
        createdAt: Date.now(),
        read: false,
        from: "CampusSwap Admin",
      });
      setNotifyMsg("");
      setNotifyUserId(null);
      alert("Notification sent!");
    } catch (e) { alert("Failed to send."); }
    setSendingNotify(false);
  }

  function exportUsersCSV() {
    const headers = ["Name", "Email", "Phone", "College", "Type", "Verified", "Suspended", "Joined"];
    const rows = users.map(u => [
      u.name || "", u.email || "", u.phone || "",
      u.college || u.childSchool || "",
      u.userType || "student",
      u.verified ? "Yes" : "No",
      u.suspended ? "Yes" : "No",
      u.createdAt ? new Date(u.createdAt).toLocaleDateString("en-IN") : "",
    ]);
    const csv = [headers, ...rows].map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `campusswap_users_${new Date().toISOString().slice(0,10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  }

  async function fetchAmbassadors() {
    try {
      const snap = await get(ref(db, "ambassadors"));
      if (snap.exists()) {
        const all = Object.entries(snap.val()).map(([id, data]) => ({ id, ...data }));
        setAmbassadors(all.sort((a, b) => new Date(b.appliedAt) - new Date(a.appliedAt)));
      } else { setAmbassadors([]); }
    } catch (e) {  }
  }

  async function approveAmbassador(id, userId, name) {
    await update(ref(db, `ambassadors/${id}`), { status: "approved" });
    if (userId) await update(ref(db, `users/${userId}`), { isAmbassador: true, verified: true, idVerificationStatus: "approved" });
    setAmbassadors(prev => prev.map(a => a.id === id ? { ...a, status: "approved" } : a));
    if (userId) {
      await push(ref(db, `notifications/${userId}`), {
        type: "ambassador",
        message: `🎉 Congratulations ${name}! Your Campus Ambassador application has been approved.`,
        createdAt: Date.now(), read: false, from: "CampusSwap Admin",
      });
    }
  }

  async function rejectAmbassador(id, userId, name) {
    await update(ref(db, `ambassadors/${id}`), { status: "rejected" });
    setAmbassadors(prev => prev.map(a => a.id === id ? { ...a, status: "rejected" } : a));
    if (userId) {
      await push(ref(db, `notifications/${userId}`), {
        type: "ambassador",
        message: `Your Campus Ambassador application was reviewed. Thank you for applying, ${name}.`,
        createdAt: Date.now(), read: false, from: "CampusSwap Admin",
      });
    }
  }

  // ─── 2FA setup ───────────────────────────────────────────────────────────────

  function startTwoFASetup() {
    const secret = new Secret({ size: 20 });
    const totp = new TOTP({ issuer: "CampusSwap", label: "Admin", algorithm: "SHA1", digits: 6, period: 30, secret });
    setTwoFATotp(totp);
    setTwoFASetupMode(true);
    setTwoFAError("");
    setTwoFAVerifyCode("");
  }

  async function confirmTwoFASetup(e) {
    e.preventDefault();
    setTwoFAError("");
    if (!twoFATotp) return;
    const delta = twoFATotp.validate({ token: twoFAVerifyCode, window: 1 });
    if (delta === null) { setTwoFAError("Invalid code. Please check your authenticator app and try again."); return; }
    try {
      await set(ref(db, "adminConfig/totpSecret"), twoFATotp.secret.base32);
      setTwoFAEnabled(true);
      setTwoFASetupMode(false);
      setTwoFATotp(null);
      setTwoFAVerifyCode("");
      setTwoFASuccess("2FA enabled! You will need your authenticator app on every login.");
    } catch (e) { setTwoFAError("Failed to save. Try again."); }
  }

  async function disableTwoFA() {
    if (!window.confirm("Disable 2FA? Anyone with your login credentials can access admin without the authenticator app.")) return;
    await remove(ref(db, "adminConfig/totpSecret"));
    setTwoFAEnabled(false);
    setTwoFASetupMode(false);
    setTwoFATotp(null);
    setTwoFASuccess("2FA has been disabled.");
  }

  // ─── Community posts ─────────────────────────────────────────────────────────

  async function fetchCommunityPosts() {
    setLoadingCommunity(true);
    try {
      const nodes = [
        { key: "notes",     label: "Notes",      titleField: "title" },
        { key: "swaps",     label: "Swap",       titleField: "offeringTitle" },
        { key: "lostFound", label: "Lost & Found",titleField: "itemName" },
        { key: "rideshare", label: "Rideshare",  titleField: "from" },
        { key: "events",    label: "Events",     titleField: "title" },
        { key: "tutoring",  label: "Tutoring",   titleField: "name" },
        { key: "giveaways", label: "Giveaway",   titleField: "title" },
      ];
      const snaps = await Promise.all(nodes.map(n => get(ref(db, n.key))));
      let all = [];
      snaps.forEach((snap, i) => {
        if (snap.exists()) {
          Object.entries(snap.val()).forEach(([id, data]) => {
            all.push({ id, _node: nodes[i].key, _label: nodes[i].label, _titleField: nodes[i].titleField, ...data });
          });
        }
      });
      all.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      setCommunityPosts(all);
    } catch (e) {  }
    setLoadingCommunity(false);
  }

  async function fetchFeedback() {
    try {
      const snap = await get(ref(db, "feedback"));
      if (snap.exists()) {
        let all = [];
        Object.entries(snap.val()).forEach(([uid, entries]) => {
          Object.entries(entries).forEach(([fid, data]) => {
            all.push({ id: fid, uid, ...data });
          });
        });
        all.sort((a, b) => b.createdAt - a.createdAt);
        setFeedbackItems(all);
      } else {
        setFeedbackItems([]);
      }
    } catch (e) { /* ignore */ }
  }

  async function markFeedbackResolved(uid, fid, feedbackType) {
    await update(ref(db, `feedback/${uid}/${fid}`), { status: "resolved", resolvedAt: Date.now() });
    await push(ref(db, `notifications/${uid}`), {
      type: "support_resolved",
      title: "Your support message has been resolved",
      body: `Your ${feedbackType || "support"} message has been reviewed and resolved by the CampusSwap team. Thank you for reaching out!`,
      createdAt: Date.now(),
      read: false,
    });
    setFeedbackItems(prev => prev.map(f => f.id === fid ? { ...f, status: "resolved" } : f));
  }

  async function approveCollegeChange(uid, fid) {
    if (!window.confirm("Approve this college change request? User will get 2 fresh change chances.")) return;
    // Reset college change count → gives user 2 fresh chances
    await update(ref(db, `users/${uid}`), { collegeChanges: 0, collegeChangeApproved: true });
    // Mark ticket resolved
    await update(ref(db, `feedback/${uid}/${fid}`), { status: "resolved", resolvedAt: Date.now() });
    // Notify user
    await push(ref(db, `notifications/${uid}`), {
      type: "college_change_approved",
      title: "College Change Approved ✅",
      body: "Your request to change college name has been approved by admin. You now have 2 new chances to update it from your Profile.",
      createdAt: Date.now(),
      read: false,
    });
    setFeedbackItems(prev => prev.map(f => f.id === fid ? { ...f, status: "resolved" } : f));
  }

  async function deleteFeedback(uid, fid) {
    if (!window.confirm("Delete this feedback entry?")) return;
    await remove(ref(db, `feedback/${uid}/${fid}`));
    setFeedbackItems(prev => prev.filter(f => f.id !== fid));
  }

  async function deleteCommunityPost(node, id) {
    if (!window.confirm("Delete this post?")) return;
    await remove(ref(db, `${node}/${id}`));
    setCommunityPosts(prev => prev.filter(p => !(p._node === node && p.id === id)));
  }

  // ─── Derived data ────────────────────────────────────────────────────────────

  // Filter out ghost entries (users with no profile data — created by lastActive update bug)
  const realUsers      = users.filter(u => u.name || u.email || u.phone);
  const pendingUsers   = realUsers.filter(u => u.idVerificationStatus === "pending");
  const verifiedUsers  = realUsers.filter(u => u.verified);
  const activeListings = listings.filter(l => l.status === "active");
  const soldListings   = listings.filter(l => l.status === "sold");

  // Analytics helpers
  const totalRevenue = soldListings.reduce((sum, l) => sum + (parseFloat(l.price) || 0), 0);

  const categoryCounts = listings.reduce((acc, l) => {
    if (l.category) acc[l.category] = (acc[l.category] || 0) + 1;
    return acc;
  }, {});
  const topCategory = Object.entries(categoryCounts).sort((a, b) => b[1] - a[1])[0];

  const collegeCounts = listings.reduce((acc, l) => {
    const c = l.sellerCollege || l.college || "Unknown";
    acc[c] = (acc[c] || 0) + 1;
    return acc;
  }, {});
  const topColleges = Object.entries(collegeCounts).sort((a, b) => b[1] - a[1]).slice(0, 5);
  const maxCollegeCount = topColleges[0]?.[1] || 1;

  const oneWeekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
  const newUsersThisWeek = users.filter(u => {
    const t = u.createdAt ? new Date(u.createdAt).getTime() : 0;
    return t >= oneWeekAgo;
  }).length;
  const newListingsThisWeek = listings.filter(l => {
    const t = l.createdAt ? new Date(l.createdAt).getTime() : 0;
    return t >= oneWeekAgo;
  }).length;

  const STAT_CARDS = [
    { label: "Total Users",     value: realUsers.length,      icon: Users,         grad: "from-blue-500 to-blue-700",    shadow: "shadow-blue-200",    tab: "users" },
    { label: "Verified Users",  value: verifiedUsers.length,  icon: ShieldCheck,   grad: "from-emerald-500 to-green-700",shadow: "shadow-green-200",   tab: "verifications" },
    { label: "Active Listings", value: activeListings.length, icon: Package,       grad: "from-indigo-500 to-indigo-700",shadow: "shadow-indigo-200",  tab: "listings" },
    { label: "Total Sold",      value: soldListings.length,   icon: DollarSign,    grad: "from-purple-500 to-purple-700",shadow: "shadow-purple-200",  tab: null },
    { label: "Pending IDs",     value: pendingUsers.length,   icon: AlertTriangle, grad: "from-orange-400 to-orange-600",shadow: "shadow-orange-200",  tab: "verifications" },
    { label: "Reports",         value: reports.length,        icon: Flag,          grad: "from-red-500 to-red-700",      shadow: "shadow-red-200",     tab: "reports" },
    { label: "Support Msgs",    value: feedbackItems.filter(f => f.status === "open").length, icon: MessageSquare, grad: "from-violet-500 to-violet-700", shadow: "shadow-violet-200", tab: "feedback" },
  ];

  const tabCounts = {
    verifications: pendingUsers.length,
    listings:      listings.length,
    users:         realUsers.length,
    parents:       parentListings.length,
    reports:       reports.length,
    ambassadors:   ambassadors.filter(a => a.status === "pending").length,
    community:     communityPosts.length,
    feedback:      feedbackItems.filter(f => f.status === "open").length,
  };

  // ─── Skeleton loader ─────────────────────────────────────────────────────────

  const Skeleton = () => (
    <div className="space-y-3">
      {[1, 2, 3].map(i => (
        <div key={i} className="h-24 bg-white rounded-2xl animate-pulse shadow-sm" />
      ))}
    </div>
  );

  // ─── Status badge helper ─────────────────────────────────────────────────────

  function StatusBadge({ status }) {
    const map = {
      active:   "bg-green-100 text-green-700 border border-green-200",
      reserved: "bg-yellow-100 text-yellow-700 border border-yellow-200",
      sold:     "bg-purple-100 text-purple-700 border border-purple-200",
    };
    return (
      <span className={`text-xs px-2.5 py-0.5 rounded-full font-semibold capitalize ${map[status] || "bg-gray-100 text-gray-500 border border-gray-200"}`}>
        {status}
      </span>
    );
  }

  // ════════════════════════════════════════════════════════════════════════════
  //  RENDER
  // ════════════════════════════════════════════════════════════════════════════

  return (
    <div className="min-h-screen bg-slate-100">

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <header className="sticky top-0 z-50">
        {/* Top accent bar */}
        <div className="h-1 bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500" />
        <div className="bg-gray-950 text-white shadow-2xl">
          <div className="max-w-5xl mx-auto px-3 py-2.5 flex items-center justify-between">
            <div className="flex items-center gap-2 min-w-0">
              <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center flex-shrink-0">
                <ShieldCheck size={16} className="text-white" />
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-1.5">
                  <p className="font-extrabold text-white text-sm tracking-tight">CampusSwap</p>
                  <span className="text-[9px] bg-blue-600 text-white px-1.5 py-0.5 rounded-md font-bold uppercase">Admin</span>
                </div>
                <p className="text-gray-400 text-[10px] truncate">Control Panel · {new Date().toLocaleDateString("en-IN", { day: "numeric", month: "short" })}</p>
              </div>
            </div>
            <div className="flex items-center gap-1.5 flex-shrink-0 ml-2">
              <button
                onClick={() => { fetchAll(); fetchCommunityPosts(); }}
                className="flex items-center gap-1 text-gray-400 hover:text-white text-xs bg-white/5 border border-white/10 px-2 py-1.5 rounded-lg transition-all"
              >
                <RefreshCw size={11} />
              </button>
              <button
                onClick={handleLogout}
                className="flex items-center gap-1 text-red-400 text-xs bg-red-500/10 border border-red-500/20 px-2 py-1.5 rounded-lg transition-all"
              >
                <LogOut size={11} /> <span className="text-xs">Out</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* ── Fetch Error Banner ─────────────────────────────────────────────── */}
      {fetchError && (
        <div className="max-w-5xl mx-auto px-4 pt-3">
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm flex items-center justify-between">
            <span>{fetchError}</span>
            <button onClick={() => fetchAll()} className="ml-3 text-xs bg-red-600 text-white px-3 py-1 rounded-lg font-semibold">Retry</button>
          </div>
        </div>
      )}

      {/* ── Stats Row ──────────────────────────────────────────────────────── */}
      <div className="max-w-5xl mx-auto px-4 pt-5 pb-2">
        <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-4 lg:grid-cols-7">
          {STAT_CARDS.map(({ label, value, icon: Icon, grad, shadow, tab: cardTab }) => (
            <div
              key={label}
              onClick={() => cardTab && setTab(cardTab)}
              className={`relative overflow-hidden bg-gradient-to-br ${grad} rounded-2xl shadow-lg ${shadow} p-3 text-white flex flex-col items-center justify-center text-center ${cardTab ? "cursor-pointer hover:scale-105 active:scale-95 transition-transform" : ""}`}
            >
              <div className="absolute -top-3 -right-3 w-14 h-14 bg-white/10 rounded-full" />
              <div className="w-8 h-8 bg-white/25 rounded-xl flex items-center justify-center mb-1.5 relative">
                <Icon size={16} className="text-white" />
              </div>
              <p className="text-2xl font-black leading-none">{value}</p>
              <p className="text-[9px] font-semibold text-white/75 mt-1 leading-tight uppercase tracking-wide">{label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* ── Tabs ───────────────────────────────────────────────────────────── */}
      <div className="max-w-5xl mx-auto px-4 pt-3 pb-1">
        <div className="flex gap-1.5 overflow-x-auto pb-1 scrollbar-hide">
          {TAB_CONFIG.map(t => {
            const isActive = tab === t.key;
            const count = tabCounts[t.key] ?? null;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-semibold whitespace-nowrap flex-shrink-0 transition-all border
                  ${isActive ? `${t.activeClass} border-transparent shadow-md` : "bg-white text-gray-500 border-gray-200 hover:border-gray-300 hover:text-gray-700"}`}
              >
                <t.icon size={13} />
                {t.label}
                {count !== null && count > 0 && (
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${isActive ? t.badgeClass + " text-white" : "bg-gray-100 text-gray-500"}`}>
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Main Content ───────────────────────────────────────────────────── */}
      <div className="max-w-5xl mx-auto px-4 py-4 pb-16 space-y-0">

        {/* Loading skeleton for data-dependent tabs */}
        {loading && ["verifications", "listings", "users", "parents", "reports", "analytics"].includes(tab) && <Skeleton />}

        {/* ── VERIFICATIONS ─────────────────────────────────────────────── */}
        {!loading && tab === "verifications" && (
          pendingUsers.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-2xl shadow-md border border-gray-100">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <CheckCircle size={32} className="text-green-500" />
              </div>
              <p className="font-bold text-gray-700 text-lg">All Caught Up!</p>
              <p className="text-sm text-gray-400 mt-1">No pending ID verifications.</p>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">{pendingUsers.length} pending verification{pendingUsers.length !== 1 ? "s" : ""}</p>
              {pendingUsers.map(user => (
                <div key={user.id} className="bg-white rounded-2xl shadow-md border border-gray-100 p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-blue-400 to-indigo-600 text-white font-bold flex items-center justify-center text-lg flex-shrink-0">
                        {user.name?.[0]?.toUpperCase() || user.email?.[0]?.toUpperCase() || "?"}
                      </div>
                      <div>
                        <p className="font-bold text-gray-800">{user.name || <span className="text-gray-400 italic">No name set</span>}</p>
                        <p className="text-xs text-gray-400">{user.email}</p>
                        <p className="text-xs text-gray-400">{user.college}</p>
                        {user.phone && <p className="text-xs text-gray-400 mt-0.5">📱 {user.phone}</p>}
                      </div>
                    </div>
                    <span className="bg-yellow-100 text-yellow-700 border border-yellow-200 text-xs px-2.5 py-1 rounded-full font-semibold">Pending</span>
                  </div>
                  {user.collegeIdUrl ? (
                    <div className="mt-3">
                      <button
                        onClick={() => setSelectedId(selectedId === user.id ? null : user.id)}
                        className="flex items-center gap-1.5 text-blue-600 text-sm font-semibold hover:text-blue-800 transition-colors"
                      >
                        {selectedId === user.id ? <><EyeOff size={14} /> Hide ID</> : <><Eye size={14} /> View College ID</>}
                      </button>
                      {selectedId === user.id && (
                        <img src={user.collegeIdUrl} alt="College ID" className="mt-2 rounded-xl max-h-56 object-contain border border-gray-200 w-full bg-gray-50" />
                      )}
                    </div>
                  ) : (
                    <p className="text-xs text-gray-400 mt-2 italic">No ID photo uploaded yet.</p>
                  )}
                  <div className="flex gap-2.5 mt-4">
                    <button
                      onClick={() => approveUser(user.id)}
                      className="flex-1 bg-gradient-to-r from-green-500 to-emerald-600 text-white py-2.5 rounded-xl text-sm font-semibold flex items-center justify-center gap-1.5 hover:from-green-600 hover:to-emerald-700 transition-all shadow-sm"
                    >
                      <CheckCircle size={16} /> Approve
                    </button>
                    <button
                      onClick={() => rejectUser(user.id)}
                      className="flex-1 bg-red-50 text-red-600 border border-red-200 py-2.5 rounded-xl text-sm font-semibold flex items-center justify-center gap-1.5 hover:bg-red-100 transition-all"
                    >
                      <XCircle size={16} /> Reject
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )
        )}

        {/* ── LISTINGS ─────────────────────────────────────────────────── */}
        {!loading && tab === "listings" && (() => {
          const filteredListings = adminListingFilter === "all"
            ? listings
            : listings.filter(l => l.status === adminListingFilter);
          return (
            <div className="space-y-3">
              <div className="flex gap-2 overflow-x-auto pb-1">
                {[{ k: "all", l: "All" }, { k: "active", l: "Active" }, { k: "reserved", l: "Reserved" }, { k: "sold", l: "Sold" }].map(({ k, l }) => (
                  <button
                    key={k}
                    onClick={() => setAdminListingFilter(k)}
                    className={`px-3.5 py-1.5 rounded-full text-xs font-semibold border flex-shrink-0 transition-all
                      ${adminListingFilter === k ? "bg-indigo-600 text-white border-indigo-600 shadow-sm" : "bg-white text-gray-500 border-gray-200 hover:border-gray-300"}`}
                  >
                    {l} ({k === "all" ? listings.length : listings.filter(x => x.status === k).length})
                  </button>
                ))}
              </div>
              {filteredListings.length === 0 ? (
                <div className="text-center py-14 bg-white rounded-2xl shadow-md border border-gray-100">
                  <Package size={36} className="mx-auto mb-2 text-gray-300" />
                  <p className="text-gray-400 font-medium">No listings found.</p>
                </div>
              ) : filteredListings.map(l => (
                <div key={l.id} className="bg-white rounded-2xl shadow-md border border-gray-100 flex overflow-hidden hover:shadow-lg transition-shadow">
                  <div className="w-20 h-20 bg-slate-100 flex-shrink-0 self-center ml-3 rounded-xl overflow-hidden">
                    {l.imageUrl
                      ? <img src={l.imageUrl} alt="" className="w-full h-full object-cover" />
                      : <div className="w-full h-full flex items-center justify-center text-gray-300"><Package size={22} /></div>}
                  </div>
                  <div className="p-3 flex-1 min-w-0">
                    <p className="font-bold text-sm text-gray-800 line-clamp-1">{l.title}</p>
                    <p className="text-indigo-600 font-extrabold text-sm mt-0.5">₹{l.price}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{l.sellerName} · {l.category}</p>
                    <div className="flex items-center justify-between mt-2">
                      <StatusBadge status={l.status} />
                      <div className="flex items-center gap-1.5">
                        <button
                          onClick={() => toggleFeatureListing(l.id, l.featured)}
                          className={`text-xs font-semibold px-2.5 py-1 rounded-lg transition-all flex items-center gap-1 ${l.featured ? "bg-yellow-100 text-yellow-700 border border-yellow-300" : "bg-gray-50 text-gray-500 border border-gray-200 hover:bg-yellow-50"}`}
                        >
                          <Star size={11} /> {l.featured ? "Unfeature" : "Feature"}
                        </button>
                        {l.status !== "sold" && (
                          <button
                            onClick={() => markListingAsSold(l.id)}
                            className="text-xs bg-purple-50 text-purple-600 border border-purple-200 font-semibold px-2.5 py-1 rounded-lg hover:bg-purple-100 transition-all flex items-center gap-1"
                          >
                            <CheckCircle size={11} /> Sold
                          </button>
                        )}
                        {(l.status === "sold" || l.status === "reserved") && (
                          <button
                            onClick={() => relistListing(l.id)}
                            className="text-xs bg-blue-50 text-blue-600 border border-blue-200 font-semibold px-2.5 py-1 rounded-lg hover:bg-blue-100 transition-all flex items-center gap-1"
                          >
                            <RefreshCw size={11} /> Relist
                          </button>
                        )}
                        <button onClick={() => deleteListing(l.id)} className="text-red-400 hover:text-red-600 p-1.5 rounded-lg hover:bg-red-50 transition-all">
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          );
        })()}

        {/* ── USERS ──────────────────────────────────────────────────────── */}
        {!loading && tab === "users" && (
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-2">
              <input
                type="text"
                value={userSearch}
                onChange={e => setUserSearch(e.target.value)}
                placeholder="Search by name, email or phone..."
                className="flex-1 border border-gray-200 rounded-xl px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
              <button onClick={exportUsersCSV} className="flex items-center gap-1.5 text-xs bg-green-600 text-white px-3 py-1.5 rounded-lg font-semibold hover:bg-green-700 transition-all flex-shrink-0">
                <Download size={12} /> Export CSV
              </button>
            </div>
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
              {userSearch
                ? `${realUsers.filter(u => [u.name, u.email, u.phone].some(v => v?.toLowerCase().includes(userSearch.toLowerCase()))).length} of ${realUsers.length} users`
                : `${realUsers.length} registered users`}
            </p>
            {realUsers
              .filter(u => !userSearch || [u.name, u.email, u.phone].some(v => v?.toLowerCase().includes(userSearch.toLowerCase())))
              .map(u => {
              const userListings = listings.filter(l => l.sellerId === u.id);
              return (
                <div
                  key={u.id}
                  className={`bg-white rounded-2xl shadow-md border p-4 transition-all ${u.suspended ? "border-red-200 bg-red-50/30" : "border-gray-100"}`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-11 h-11 rounded-xl font-bold flex items-center justify-center flex-shrink-0 text-base
                      ${u.suspended ? "bg-red-200 text-red-700" : "bg-gradient-to-br from-blue-400 to-indigo-600 text-white"}`}>
                      {u.name?.[0]?.toUpperCase() || u.email?.[0]?.toUpperCase() || u.phone?.[0] || "?"}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <p className="font-bold text-sm text-gray-800 line-clamp-1">{u.name || u.email || u.phone || <span className="text-gray-400 italic text-xs">Incomplete profile</span>}</p>
                        {u.verified && <ShieldCheck size={13} className="text-green-500 flex-shrink-0" />}
                        {u.suspended && (
                          <span className="bg-red-100 text-red-600 border border-red-200 text-[10px] px-2 py-0.5 rounded-full font-bold flex items-center gap-0.5">
                            <Ban size={9} /> Suspended
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-400 line-clamp-1">{u.email}</p>
                      <p className="text-xs text-gray-400 line-clamp-1">{u.college || u.childSchool}</p>
                    </div>
                    <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold border
                        ${u.verified ? "bg-green-100 text-green-700 border-green-200"
                          : u.idVerificationStatus === "pending" ? "bg-yellow-100 text-yellow-700 border-yellow-200"
                          : "bg-gray-100 text-gray-500 border-gray-200"}`}>
                        {u.userType === "parent" ? "Parent" : u.verified ? "Verified" : u.idVerificationStatus === "pending" ? "Pending" : "Unverified"}
                      </span>
                      {u.suspended ? (
                        <button
                          onClick={() => unsuspendUser(u.id)}
                          className="text-[10px] bg-green-100 text-green-700 border border-green-200 px-2 py-0.5 rounded-full font-semibold hover:bg-green-200 transition-all"
                        >
                          Unsuspend
                        </button>
                      ) : (
                        <button
                          onClick={() => suspendUser(u.id)}
                          className="text-[10px] bg-red-100 text-red-600 border border-red-200 px-2 py-0.5 rounded-full font-semibold hover:bg-red-200 transition-all flex items-center gap-0.5"
                        >
                          <Ban size={9} /> Suspend
                        </button>
                      )}
                      {u.verified ? (
                        <button
                          onClick={() => rejectUser(u.id)}
                          className="text-[10px] bg-yellow-100 text-yellow-700 border border-yellow-200 px-2 py-0.5 rounded-full font-semibold hover:bg-yellow-200 transition-all flex items-center gap-0.5"
                        >
                          <ShieldCheck size={9} /> Unverify
                        </button>
                      ) : (
                        <button
                          onClick={() => approveUser(u.id)}
                          className="text-[10px] bg-green-100 text-green-700 border border-green-200 px-2 py-0.5 rounded-full font-semibold hover:bg-green-200 transition-all flex items-center gap-0.5"
                        >
                          <ShieldCheck size={9} /> Verify
                        </button>
                      )}
                      <button
                        onClick={() => setNotifyUserId(notifyUserId === u.id ? null : u.id)}
                        className="text-[10px] bg-blue-100 text-blue-600 border border-blue-200 px-2 py-0.5 rounded-full font-semibold hover:bg-blue-200 transition-all flex items-center gap-0.5"
                      >
                        <Send size={9} /> Notify
                      </button>
                      <button
                        onClick={() => deleteUser(u.id, u.name)}
                        className="text-[10px] bg-gray-100 text-red-500 border border-red-200 px-2 py-0.5 rounded-full font-semibold hover:bg-red-50 transition-all flex items-center gap-0.5"
                      >
                        <UserX size={9} /> Delete
                      </button>
                    </div>
                  </div>
                  {notifyUserId === u.id && (
                    <div className="mt-3 border-t border-blue-100 pt-3">
                      <p className="text-xs font-semibold text-blue-600 mb-2">Send notification to {u.name}</p>
                      <div className="flex gap-2">
                        <input
                          value={notifyMsg}
                          onChange={e => setNotifyMsg(e.target.value)}
                          placeholder="Type message..."
                          className="flex-1 border border-blue-200 rounded-xl px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-blue-400"
                        />
                        <button onClick={sendNotificationToUser} disabled={sendingNotify || !notifyMsg.trim()}
                          className="bg-blue-600 text-white px-3 py-2 rounded-xl text-xs font-bold disabled:opacity-50">
                          {sendingNotify ? "..." : "Send"}
                        </button>
                      </div>
                    </div>
                  )}
                  {userListings.length > 0 && (
                    <div className="mt-3 border-t border-gray-100 pt-3">
                      <p className="text-xs font-semibold text-gray-400 mb-2 uppercase tracking-wider">Listings ({userListings.length})</p>
                      <div className="space-y-1.5">
                        {userListings.map(l => (
                          <div key={l.id} className="flex items-center justify-between bg-slate-50 rounded-xl px-3 py-2 border border-gray-100">
                            <div className="flex items-center gap-2 min-w-0">
                              {l.imageUrl && <img src={l.imageUrl} alt="" className="w-8 h-8 rounded-lg object-cover flex-shrink-0" />}
                              <div className="min-w-0">
                                <p className="text-xs font-semibold text-gray-700 line-clamp-1">{l.title}</p>
                                <p className="text-[10px] text-gray-400">₹{l.price} · {l.status}</p>
                              </div>
                            </div>
                            <button onClick={() => deleteListing(l.id)} className="text-red-400 hover:text-red-600 flex-shrink-0 ml-2 p-1 rounded-lg hover:bg-red-50 transition-all">
                              <Trash2 size={14} />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
            {realUsers.length > 0 && userSearch && !realUsers.some(u => [u.name, u.email, u.phone].some(v => v?.toLowerCase().includes(userSearch.toLowerCase()))) && (
              <div className="text-center py-10 bg-white rounded-2xl border border-gray-100">
                <p className="text-gray-400 text-sm">No users found for "<strong>{userSearch}</strong>"</p>
              </div>
            )}
          </div>
        )}

        {/* ── PARENTS ────────────────────────────────────────────────────── */}
        {!loading && tab === "parents" && (
          <div className="space-y-3">
            {parentListings.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-2xl shadow-md border border-gray-100">
                <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Baby size={32} className="text-amber-500" />
                </div>
                <p className="font-bold text-gray-700">No Parent Listings Yet</p>
                <p className="text-sm text-gray-400 mt-1">Parent zone listings will appear here.</p>
              </div>
            ) : parentListings.map(l => (
              <div key={l.id} className="bg-white rounded-2xl shadow-md border border-gray-100 flex overflow-hidden hover:shadow-lg transition-shadow">
                <div className="w-20 h-20 bg-slate-100 flex-shrink-0 self-center ml-3 rounded-xl overflow-hidden">
                  {l.imageUrl
                    ? <img src={l.imageUrl} alt="" className="w-full h-full object-cover" />
                    : <div className="w-full h-full flex items-center justify-center text-gray-300"><Package size={22} /></div>}
                </div>
                <div className="p-3 flex-1 min-w-0">
                  <p className="font-bold text-sm text-gray-800 line-clamp-1">{l.title}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="bg-amber-100 text-amber-700 border border-amber-200 text-xs font-bold px-2 py-0.5 rounded-full">{l.childClass}</span>
                    <p className="text-indigo-600 font-extrabold text-sm">{l.price === 0 ? "Free" : `₹${l.price}`}</p>
                  </div>
                  <p className="text-xs text-gray-400 mt-0.5 line-clamp-1">{l.schoolName}</p>
                  <div className="flex items-center justify-between mt-2">
                    <StatusBadge status={l.status} />
                    <button onClick={() => deleteParentListing(l.id)} className="text-red-400 hover:text-red-600 p-1.5 rounded-lg hover:bg-red-50 transition-all">
                      <Trash2 size={15} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── REPORTS ────────────────────────────────────────────────────── */}
        {!loading && tab === "reports" && (
          <div className="space-y-4">
            {reports.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-2xl shadow-md border border-gray-100">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <CheckCircle size={32} className="text-green-500" />
                </div>
                <p className="font-bold text-gray-700 text-lg">No Reports</p>
                <p className="text-sm text-gray-400 mt-1">No active reports to review.</p>
              </div>
            ) : (
              <>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">{reports.length} active report{reports.length !== 1 ? "s" : ""}</p>
                {reports.map(report => {
                  const reportedListing = listings.find(l => l.id === report.listingId);
                  return (
                    <div key={report.id} className="bg-white rounded-2xl shadow-md border border-red-100 p-4">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center flex-shrink-0">
                          <Flag size={18} className="text-red-500" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-bold text-gray-800 line-clamp-1">
                            {report.listingTitle || reportedListing?.title || "Listing #" + (report.listingId || "?")}
                          </p>
                          <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                            <span className="bg-red-50 text-red-600 border border-red-200 text-[10px] px-2 py-0.5 rounded-full font-semibold">{report.reason || "Reported"}</span>
                            {report.createdAt && (
                              <span className="text-[10px] text-gray-400">
                                {new Date(report.createdAt).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                              </span>
                            )}
                          </div>
                          {report.reporterName && (
                            <p className="text-xs text-gray-400 mt-1">Reported by: <span className="font-medium text-gray-600">{report.reporterName}</span></p>
                          )}
                          {report.reporterEmail && (
                            <p className="text-xs text-gray-400">{report.reporterEmail}</p>
                          )}
                          {report.description && (
                            <p className="text-xs text-gray-500 mt-2 bg-slate-50 rounded-lg px-3 py-2 border border-gray-100 italic">"{report.description}"</p>
                          )}
                        </div>
                      </div>
                      {reportedListing && (
                        <div className="mt-3 bg-slate-50 rounded-xl p-3 border border-gray-100 flex items-center gap-2">
                          {reportedListing.imageUrl && (
                            <img src={reportedListing.imageUrl} alt="" className="w-10 h-10 rounded-lg object-cover flex-shrink-0" />
                          )}
                          <div className="min-w-0">
                            <p className="text-xs font-semibold text-gray-700 line-clamp-1">{reportedListing.title}</p>
                            <p className="text-[10px] text-gray-400">₹{reportedListing.price} · {reportedListing.sellerName}</p>
                          </div>
                        </div>
                      )}
                      <div className="flex gap-2 mt-3">
                        <button
                          onClick={() => deleteListingFromReport(report.id, report.listingId)}
                          disabled={!report.listingId}
                          className="flex-1 bg-gradient-to-r from-red-500 to-red-700 text-white py-2.5 rounded-xl text-xs font-bold flex items-center justify-center gap-1.5 hover:from-red-600 hover:to-red-800 transition-all shadow-sm disabled:opacity-40"
                        >
                          <Trash2 size={14} /> Delete Listing
                        </button>
                        <button
                          onClick={() => dismissReport(report.id)}
                          className="flex-1 bg-slate-100 text-slate-600 border border-slate-200 py-2.5 rounded-xl text-xs font-bold flex items-center justify-center gap-1.5 hover:bg-slate-200 transition-all"
                        >
                          <XCircle size={14} /> Dismiss Report
                        </button>
                      </div>
                    </div>
                  );
                })}
              </>
            )}
          </div>
        )}

        {/* ── ANALYTICS ─────────────────────────────────────────────────── */}
        {!loading && tab === "analytics" && (
          <div className="space-y-4">
            {/* Quick stats */}
            <div className="grid grid-cols-2 gap-3">
              {[
                { label: "Total Revenue", value: `₹${totalRevenue.toLocaleString("en-IN")}`, icon: DollarSign, grad: "from-purple-500 to-purple-700", sub: `from ${soldListings.length} sold items` },
                { label: "Top Category",  value: topCategory ? topCategory[0] : "N/A", icon: Star, grad: "from-amber-400 to-orange-500", sub: topCategory ? `${topCategory[1]} listings` : "No data yet" },
                { label: "New Users",     value: newUsersThisWeek, icon: TrendingUp, grad: "from-blue-500 to-blue-700", sub: "this week" },
                { label: "New Listings",  value: newListingsThisWeek, icon: Package, grad: "from-indigo-500 to-indigo-700", sub: "this week" },
              ].map(({ label, value, icon: Icon, grad, sub }) => (
                <div key={label} className={`bg-gradient-to-br ${grad} rounded-2xl shadow-md p-4 text-white`}>
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-[10px] font-semibold text-white/70 uppercase tracking-wider">{label}</p>
                      <p className="text-xl font-extrabold mt-1 leading-tight">{value}</p>
                      <p className="text-[10px] text-white/60 mt-0.5">{sub}</p>
                    </div>
                    <div className="w-9 h-9 bg-white/20 rounded-xl flex items-center justify-center">
                      <Icon size={18} className="text-white" />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Category breakdown */}
            <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-4">
              <div className="flex items-center gap-2 mb-4">
                <BarChart2 size={18} className="text-purple-600" />
                <h3 className="font-bold text-gray-800">Listings by Category</h3>
              </div>
              {Object.entries(categoryCounts).length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-4">No listing data yet.</p>
              ) : (
                <div className="space-y-2.5">
                  {Object.entries(categoryCounts)
                    .sort((a, b) => b[1] - a[1])
                    .map(([cat, count]) => {
                      const pct = Math.round((count / listings.length) * 100);
                      return (
                        <div key={cat}>
                          <div className="flex justify-between items-center mb-1">
                            <p className="text-xs font-semibold text-gray-600">{cat}</p>
                            <p className="text-xs text-gray-400">{count} ({pct}%)</p>
                          </div>
                          <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-gradient-to-r from-purple-500 to-indigo-500 rounded-full transition-all"
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                        </div>
                      );
                    })}
                </div>
              )}
            </div>

            {/* College-wise listings */}
            <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-4">
              <div className="flex items-center gap-2 mb-4">
                <TrendingUp size={18} className="text-blue-600" />
                <h3 className="font-bold text-gray-800">Top 5 Colleges by Listings</h3>
              </div>
              {topColleges.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-4">No college data yet.</p>
              ) : (
                <div className="space-y-2.5">
                  {topColleges.map(([college, count], idx) => {
                    const pct = Math.round((count / maxCollegeCount) * 100);
                    const gradients = [
                      "from-blue-500 to-blue-600",
                      "from-indigo-500 to-indigo-600",
                      "from-purple-500 to-purple-600",
                      "from-teal-500 to-teal-600",
                      "from-cyan-500 to-cyan-600",
                    ];
                    return (
                      <div key={college}>
                        <div className="flex justify-between items-center mb-1">
                          <div className="flex items-center gap-1.5">
                            <span className="text-[10px] font-bold text-gray-400 w-4">#{idx + 1}</span>
                            <p className="text-xs font-semibold text-gray-600 line-clamp-1">{college}</p>
                          </div>
                          <p className="text-xs text-gray-400 flex-shrink-0 ml-2">{count}</p>
                        </div>
                        <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                          <div
                            className={`h-full bg-gradient-to-r ${gradients[idx]} rounded-full transition-all`}
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* User growth summary */}
            <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-4">
              <div className="flex items-center gap-2 mb-4">
                <Users size={18} className="text-green-600" />
                <h3 className="font-bold text-gray-800">User Overview</h3>
              </div>
              <div className="grid grid-cols-3 gap-3 text-center">
                {[
                  { label: "Total",      value: users.length,           color: "text-blue-600" },
                  { label: "Verified",   value: verifiedUsers.length,   color: "text-green-600" },
                  { label: "Suspended",  value: users.filter(u => u.suspended).length, color: "text-red-600" },
                ].map(({ label, value, color }) => (
                  <div key={label} className="bg-slate-50 rounded-xl p-3 border border-gray-100">
                    <p className={`text-2xl font-extrabold ${color}`}>{value}</p>
                    <p className="text-[10px] text-gray-400 mt-0.5 font-medium">{label}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* College-wise Users */}
            <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-4">
              <div className="flex items-center gap-2 mb-4">
                <TrendingUp size={18} className="text-indigo-600" />
                <h3 className="font-bold text-gray-800">Top Colleges by Users</h3>
              </div>
              {(() => {
                const userCollegeCounts = realUsers.reduce((acc, u) => {
                  const c = u.college || "Unknown";
                  acc[c] = (acc[c] || 0) + 1;
                  return acc;
                }, {});
                const topUserColleges = Object.entries(userCollegeCounts).sort((a, b) => b[1] - a[1]).slice(0, 8);
                const maxCount = topUserColleges[0]?.[1] || 1;
                return topUserColleges.length === 0
                  ? <p className="text-sm text-gray-400 text-center py-4">No user data yet.</p>
                  : (
                    <div className="space-y-2.5">
                      {topUserColleges.map(([college, count]) => (
                        <div key={college}>
                          <div className="flex justify-between items-center mb-1">
                            <p className="text-xs font-semibold text-gray-600 line-clamp-1">{college}</p>
                            <p className="text-xs text-gray-400">{count} users</p>
                          </div>
                          <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                            <div className="h-full bg-gradient-to-r from-indigo-500 to-blue-500 rounded-full" style={{ width: `${Math.round((count / maxCount) * 100)}%` }} />
                          </div>
                        </div>
                      ))}
                    </div>
                  );
              })()}
            </div>

            {/* Publish Stats to Users */}
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl shadow-md border border-green-200 p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-green-100 rounded-xl flex items-center justify-center">
                    <TrendingUp size={16} className="text-green-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-800 text-sm">Stats Banner on Home</h3>
                    <p className="text-xs text-gray-400">Edit the numbers shown to users</p>
                  </div>
                </div>
                <button
                  onClick={() => setStatsForm(f => ({ ...f, visible: !f.visible }))}
                  className={`text-xs font-bold px-3 py-1.5 rounded-xl border transition ${statsForm.visible ? "bg-green-100 text-green-700 border-green-200" : "bg-gray-100 text-gray-500 border-gray-200"}`}
                >
                  {statsForm.visible ? "👁 Visible" : "🙈 Hidden"}
                </button>
              </div>

              <button
                type="button"
                onClick={() => {
                  const colleges = Object.keys(realUsers.reduce((a, u) => { if (u.college) a[u.college] = 1; return a; }, {})).length;
                  setStatsForm(f => ({ ...f, totalUsers: String(realUsers.length), totalListings: String(listings.length), totalColleges: String(colleges) }));
                }}
                className="w-full mb-3 bg-white border border-green-200 text-green-700 text-xs font-semibold py-2 rounded-xl hover:bg-green-50"
              >
                ↻ Auto-fill from real counts ({realUsers.length} students · {listings.length} listings)
              </button>

              <div className="grid grid-cols-3 gap-2 mb-3">
                {[
                  { key: "totalUsers", label: "Students", color: "text-blue-600" },
                  { key: "totalListings", label: "Listings", color: "text-indigo-600" },
                  { key: "totalColleges", label: "Colleges", color: "text-purple-600" },
                ].map(({ key, label, color }) => (
                  <div key={key} className="bg-white rounded-xl p-2 border border-green-100 text-center">
                    <input
                      type="number"
                      min="0"
                      value={statsForm[key]}
                      onChange={e => setStatsForm(f => ({ ...f, [key]: e.target.value }))}
                      className={`w-full text-center text-lg font-extrabold ${color} bg-transparent outline-none border-b border-gray-200 focus:border-green-400 mb-1`}
                      placeholder="0"
                    />
                    <p className="text-[10px] text-gray-400">{label}</p>
                  </div>
                ))}
              </div>

              <div className="flex gap-2">
                <button
                  onClick={async () => {
                    setSavingStats(true);
                    try {
                      await update(ref(db, "appStats"), {
                        totalUsers: Number(statsForm.totalUsers) || 0,
                        totalListings: Number(statsForm.totalListings) || 0,
                        totalColleges: Number(statsForm.totalColleges) || 0,
                        visible: statsForm.visible,
                        updatedAt: Date.now(),
                      });
                      alert(statsForm.visible ? "Stats saved — banner is live!" : "Stats saved — banner is hidden.");
                    } catch (e) { alert("Failed to save stats."); }
                    setSavingStats(false);
                  }}
                  disabled={savingStats}
                  className="flex-1 bg-green-600 text-white py-2.5 rounded-xl font-semibold text-sm hover:bg-green-700 disabled:opacity-50"
                >
                  {savingStats ? "Saving..." : "Save & Publish →"}
                </button>
                <button
                  onClick={async () => {
                    if (!window.confirm("Delete the stats banner entirely? Users won't see it until you publish again.")) return;
                    await remove(ref(db, "appStats"));
                    setStatsForm({ totalUsers: "", totalListings: "", totalColleges: "", visible: true });
                    alert("Stats banner deleted.");
                  }}
                  className="bg-red-50 text-red-600 border border-red-200 px-4 py-2.5 rounded-xl font-semibold text-sm hover:bg-red-100 flex items-center justify-center"
                  title="Delete stats banner"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>

            {/* Referral Leaderboard */}
            <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-4">
              <div className="flex items-center gap-2 mb-4">
                <Award size={18} className="text-orange-500" />
                <h3 className="font-bold text-gray-800">Referral Leaderboard</h3>
              </div>
              {(() => {
                const referrers = users
                  .filter(u => u.referralCount && u.referralCount > 0)
                  .sort((a, b) => (b.referralCount || 0) - (a.referralCount || 0))
                  .slice(0, 10);
                if (referrers.length === 0) {
                  return <p className="text-sm text-gray-400 text-center py-4">No referral data yet.</p>;
                }
                const medals = ["🥇", "🥈", "🥉"];
                return (
                  <div className="space-y-2">
                    {referrers.map((u, idx) => (
                      <div key={u.id} className="flex items-center gap-3 bg-slate-50 rounded-xl px-3 py-2.5 border border-gray-100">
                        <span className="text-base w-6 text-center flex-shrink-0">{medals[idx] || `#${idx + 1}`}</span>
                        <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-orange-400 to-amber-500 text-white font-bold flex items-center justify-center flex-shrink-0 text-sm">
                          {u.name?.[0]?.toUpperCase() || "?"}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-bold text-gray-800 line-clamp-1">{u.name}</p>
                          <p className="text-[10px] text-gray-400 line-clamp-1">{u.college}</p>
                        </div>
                        <div className="flex-shrink-0 text-right">
                          <p className="text-sm font-extrabold text-orange-600">{u.referralCount}</p>
                          <p className="text-[10px] text-gray-400">referrals</p>
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
          </div>
        )}

        {/* ── NOTICES + BROADCAST ───────────────────────────────────────── */}
        {tab === "notices" && (
          <div className="space-y-4">
            {/* Broadcast */}
            <div className="bg-gradient-to-br from-pink-50 to-rose-50 rounded-2xl shadow-md border border-pink-200 p-4">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 bg-pink-100 rounded-xl flex items-center justify-center">
                  <Send size={16} className="text-pink-600" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-800 text-sm">Broadcast to All Users</h3>
                  <p className="text-xs text-gray-400">Sends a push notification to every user's inbox</p>
                </div>
              </div>
              <textarea
                value={broadcastMsg}
                onChange={e => setBroadcastMsg(e.target.value)}
                rows={3}
                placeholder="Type your broadcast message here..."
                className="w-full border border-pink-200 rounded-xl px-4 py-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-pink-400 bg-white"
              />
              <button
                onClick={broadcastToAllUsers}
                disabled={broadcasting || !broadcastMsg.trim()}
                className="mt-3 w-full bg-gradient-to-r from-pink-500 to-rose-600 text-white py-3 rounded-xl font-bold text-sm flex items-center justify-center gap-2 hover:from-pink-600 hover:to-rose-700 transition-all shadow-sm disabled:opacity-50"
              >
                <Send size={15} />
                {broadcasting ? "Broadcasting..." : `Broadcast to ${users.length} Users`}
              </button>
            </div>

            {/* Daily Deal */}
            <div className="bg-gradient-to-br from-yellow-50 to-orange-50 rounded-2xl shadow-md border border-yellow-200 p-4">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 bg-yellow-100 rounded-xl flex items-center justify-center">
                  <span className="text-yellow-600 font-bold text-base">⚡</span>
                </div>
                <div>
                  <h3 className="font-bold text-gray-800 text-sm">Set Daily Deal</h3>
                  <p className="text-xs text-gray-400">Show a listing as "Deal of the Day" on the Home screen</p>
                </div>
              </div>
              <input
                value={dailyDealListingId}
                onChange={e => setDailyDealListingId(e.target.value)}
                placeholder="Enter listing ID (from Firebase)"
                className="w-full border border-yellow-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400 bg-white mb-3"
              />
              <div className="flex gap-2">
                <button
                  onClick={saveDailyDeal}
                  disabled={savingDailyDeal || !dailyDealListingId.trim()}
                  className="flex-1 bg-gradient-to-r from-yellow-500 to-orange-500 text-white py-2.5 rounded-xl font-bold text-sm disabled:opacity-50"
                >
                  {savingDailyDeal ? "Saving..." : "Set as Daily Deal"}
                </button>
                <button
                  onClick={clearDailyDeal}
                  className="px-4 bg-gray-100 text-gray-600 py-2.5 rounded-xl font-bold text-sm hover:bg-gray-200"
                >
                  Clear
                </button>
              </div>
            </div>

            {/* Pending Notices */}
            {pendingNotices.length > 0 && (
              <div className="bg-white rounded-2xl shadow-md border border-orange-200 p-4">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 bg-orange-100 rounded-xl flex items-center justify-center">
                    <AlertTriangle size={16} className="text-orange-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-800 text-sm">Pending Approval ({pendingNotices.length})</h3>
                    <p className="text-xs text-gray-400">Notices submitted by users waiting for review</p>
                  </div>
                </div>
                <div className="space-y-3">
                  {pendingNotices.map(n => (
                    <div key={n.id} className="border border-gray-100 rounded-xl p-3">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-bold bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">{n.category}</span>
                        <span className="text-xs text-gray-400">{n.college}</span>
                        <span className="text-xs text-gray-400">by {n.postedByName}</span>
                      </div>
                      <p className="font-semibold text-sm text-gray-800">{n.title}</p>
                      <p className="text-xs text-gray-600 mt-0.5 line-clamp-2">{n.content}</p>
                      <div className="flex gap-2 mt-3">
                        <button onClick={() => approveNotice(n)}
                          className="flex-1 bg-green-600 text-white py-2 rounded-lg text-xs font-bold flex items-center justify-center gap-1">
                          <CheckCircle size={13} /> Approve
                        </button>
                        <button onClick={() => rejectNotice(n.id)}
                          className="flex-1 bg-red-500 text-white py-2 rounded-lg text-xs font-bold flex items-center justify-center gap-1">
                          <XCircle size={13} /> Reject
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Post Notice */}
            <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-4">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 bg-indigo-100 rounded-xl flex items-center justify-center">
                  <MessageSquare size={16} className="text-indigo-600" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-800 text-sm">Post Official Notice</h3>
                  <p className="text-xs text-gray-400">Visible on the Notice Board for all colleges</p>
                </div>
              </div>
              <form onSubmit={postNotice} className="space-y-3">
                <select
                  value={noticeForm.category}
                  onChange={e => setNoticeForm({ ...noticeForm, category: e.target.value })}
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 bg-slate-50"
                >
                  {["General", "Exam Schedule", "Fee Deadline", "Event", "Holiday", "Placement", "Emergency"].map(c => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
                <input
                  value={noticeForm.title}
                  onChange={e => setNoticeForm({ ...noticeForm, title: e.target.value })}
                  required
                  placeholder="Notice title"
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                />
                <textarea
                  value={noticeForm.content}
                  onChange={e => setNoticeForm({ ...noticeForm, content: e.target.value })}
                  required
                  rows={4}
                  placeholder="Notice content..."
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-indigo-400"
                />
                <button
                  type="submit"
                  disabled={postingNotice}
                  className="w-full bg-gradient-to-r from-indigo-600 to-indigo-700 text-white py-3 rounded-xl font-bold text-sm flex items-center justify-center gap-2 hover:from-indigo-700 hover:to-indigo-800 transition-all shadow-sm disabled:opacity-50"
                >
                  <Bell size={15} />
                  {postingNotice ? "Posting..." : "Post to All Colleges"}
                </button>
              </form>
            </div>
          </div>
        )}

        {/* ── AMBASSADORS ────────────────────────────────────────────────── */}
        {tab === "ambassadors" && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                {ambassadors.length} application{ambassadors.length !== 1 ? "s" : ""}
              </p>
              <button
                onClick={fetchAmbassadors}
                className="flex items-center gap-1.5 text-xs bg-orange-600 text-white px-3 py-1.5 rounded-lg font-semibold hover:bg-orange-700 transition-all"
              >
                <RefreshCw size={12} /> Refresh
              </button>
            </div>
            {ambassadors.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-2xl shadow-md border border-gray-100">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Award size={32} className="text-orange-500" />
                </div>
                <p className="font-bold text-gray-700">No Applications Yet</p>
                <p className="text-sm text-gray-400 mt-1">Ambassador applications will appear here.</p>
              </div>
            ) : (
              <>
                {["pending", "approved", "rejected"].map(statusGroup => {
                  const group = ambassadors.filter(a => a.status === statusGroup);
                  if (group.length === 0) return null;
                  const statusColors = {
                    pending:  { badge: "bg-yellow-100 text-yellow-700 border-yellow-200", header: "text-yellow-700" },
                    approved: { badge: "bg-green-100 text-green-700 border-green-200",   header: "text-green-700" },
                    rejected: { badge: "bg-red-100 text-red-600 border-red-200",         header: "text-red-600" },
                  };
                  return (
                    <div key={statusGroup}>
                      <p className={`text-xs font-bold uppercase tracking-wider mb-2 ${statusColors[statusGroup].header}`}>
                        {statusGroup} ({group.length})
                      </p>
                      <div className="space-y-3">
                        {group.map(a => (
                          <div key={a.id} className="bg-white rounded-2xl shadow-md border border-gray-100 p-4">
                            <div className="flex items-start gap-3">
                              <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-orange-400 to-amber-500 text-white font-bold flex items-center justify-center flex-shrink-0 text-lg">
                                {a.name?.[0]?.toUpperCase() || "?"}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <p className="font-bold text-gray-800 text-sm">{a.name}</p>
                                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold border ${statusColors[statusGroup].badge}`}>
                                    {statusGroup}
                                  </span>
                                </div>
                                <p className="text-xs text-gray-400 mt-0.5">{a.email}</p>
                                <p className="text-xs text-gray-400">{a.college}</p>
                                {a.appliedAt && (
                                  <p className="text-[10px] text-gray-400 mt-0.5">
                                    Applied: {new Date(a.appliedAt).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                                  </p>
                                )}
                              </div>
                            </div>
                            {a.reason && (
                              <div className="mt-3 bg-orange-50 rounded-xl p-3 border border-orange-100">
                                <p className="text-xs font-semibold text-orange-700 mb-1">Why they want to be an ambassador:</p>
                                <p className="text-xs text-gray-600 italic">"{a.reason}"</p>
                              </div>
                            )}
                            {a.socialHandle && (
                              <p className="text-xs text-gray-500 mt-2">
                                <span className="font-semibold">Social:</span> {a.socialHandle}
                              </p>
                            )}
                            {statusGroup === "pending" && (
                              <div className="flex gap-2 mt-4">
                                <button
                                  onClick={() => approveAmbassador(a.id, a.userId, a.name)}
                                  className="flex-1 bg-gradient-to-r from-green-500 to-emerald-600 text-white py-2.5 rounded-xl text-sm font-semibold flex items-center justify-center gap-1.5 hover:from-green-600 hover:to-emerald-700 transition-all shadow-sm"
                                >
                                  <CheckCircle size={15} /> Approve
                                </button>
                                <button
                                  onClick={() => rejectAmbassador(a.id, a.userId, a.name)}
                                  className="flex-1 bg-red-50 text-red-600 border border-red-200 py-2.5 rounded-xl text-sm font-semibold flex items-center justify-center gap-1.5 hover:bg-red-100 transition-all"
                                >
                                  <XCircle size={15} /> Reject
                                </button>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </>
            )}
          </div>
        )}

        {/* ── FEATURES ───────────────────────────────────────────────────── */}
        {tab === "features" && (
          <div className="space-y-3">
            <div className="bg-teal-50 border border-teal-200 rounded-2xl p-3 text-sm text-teal-700 flex items-start gap-2">
              <AlertTriangle size={15} className="mt-0.5 flex-shrink-0 text-teal-500" />
              Toggle features on/off. Changes apply instantly for all users after saving.
            </div>
            <div className="bg-white rounded-2xl shadow-md border border-gray-100 overflow-hidden divide-y divide-gray-50">
              {Object.entries(FEATURE_LABELS).map(([key, { label, desc }]) => (
                <div key={key} className="flex items-center justify-between px-4 py-3.5 hover:bg-slate-50 transition-colors">
                  <div className="flex-1 min-w-0 mr-3">
                    <p className="font-semibold text-sm text-gray-800">{label}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{desc}</p>
                  </div>
                  <button onClick={() => toggleFeature(key)} className="flex-shrink-0">
                    {appSettings.features[key]
                      ? <ToggleRight size={36} className="text-teal-500" />
                      : <ToggleLeft size={36} className="text-gray-300" />}
                  </button>
                </div>
              ))}
            </div>
            <button
              onClick={handleSaveSettings}
              disabled={savingSettings}
              className={`w-full py-3.5 rounded-2xl font-bold flex items-center justify-center gap-2 transition-all shadow-sm disabled:opacity-50
                ${settingsSaved ? "bg-gradient-to-r from-green-500 to-emerald-600 text-white" : "bg-gradient-to-r from-teal-600 to-teal-700 text-white hover:from-teal-700 hover:to-teal-800"}`}
            >
              <Save size={18} />
              {savingSettings ? "Saving..." : settingsSaved ? "Saved!" : "Save Feature Settings"}
            </button>
          </div>
        )}

        {/* ── SETTINGS ───────────────────────────────────────────────────── */}
        {tab === "settings" && (
          <div className="space-y-4">
            {/* Maintenance Mode */}
            <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 bg-orange-100 rounded-xl flex items-center justify-center">
                    <AlertTriangle size={17} className="text-orange-500" />
                  </div>
                  <div>
                    <p className="font-bold text-gray-800 text-sm">Maintenance Mode</p>
                    <p className="text-xs text-gray-400 mt-0.5">Shows maintenance screen to all users</p>
                  </div>
                </div>
                <button onClick={() => setAppSettings(p => ({ ...p, maintenance: !p.maintenance }))}>
                  {appSettings.maintenance
                    ? <ToggleRight size={36} className="text-red-500" />
                    : <ToggleLeft size={36} className="text-gray-300" />}
                </button>
              </div>
              {appSettings.maintenance && (
                <input
                  value={appSettings.maintenanceMessage}
                  onChange={e => setAppSettings(p => ({ ...p, maintenanceMessage: e.target.value }))}
                  placeholder="Maintenance message for users..."
                  className="w-full border border-orange-200 rounded-xl px-4 py-2.5 text-sm mt-3 focus:outline-none focus:ring-2 focus:ring-orange-400 bg-orange-50"
                />
              )}
            </div>

            {/* Welcome Message */}
            <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-4">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-9 h-9 bg-blue-100 rounded-xl flex items-center justify-center">
                  <MessageSquare size={17} className="text-blue-500" />
                </div>
                <div>
                  <p className="font-bold text-gray-800 text-sm">Welcome Banner</p>
                  <p className="text-xs text-gray-400 mt-0.5">Shown on home screen (leave empty to hide)</p>
                </div>
              </div>
              <textarea
                value={appSettings.welcomeMessage}
                onChange={e => setAppSettings(p => ({ ...p, welcomeMessage: e.target.value }))}
                placeholder="e.g. Welcome to CampusSwap! Your campus marketplace is now live."
                rows={3}
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
            </div>

            {/* App Version */}
            <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-4">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-9 h-9 bg-slate-100 rounded-xl flex items-center justify-center">
                  <Settings size={17} className="text-slate-500" />
                </div>
                <div>
                  <p className="font-bold text-gray-800 text-sm">App Version</p>
                  <p className="text-xs text-gray-400 mt-0.5">Displayed in the app footer</p>
                </div>
              </div>
              <input
                value={appSettings.appVersion}
                onChange={e => setAppSettings(p => ({ ...p, appVersion: e.target.value }))}
                placeholder="1.0.0"
                className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
              />
            </div>

            <button
              onClick={handleSaveSettings}
              disabled={savingSettings}
              className={`w-full py-3.5 rounded-2xl font-bold flex items-center justify-center gap-2 transition-all shadow-sm disabled:opacity-50
                ${settingsSaved ? "bg-gradient-to-r from-green-500 to-emerald-600 text-white" : "bg-gradient-to-r from-slate-700 to-slate-900 text-white hover:from-slate-800 hover:to-black"}`}
            >
              <Save size={18} />
              {savingSettings ? "Saving..." : settingsSaved ? "Settings Saved!" : "Save Settings"}
            </button>

            {/* ── Two-Factor Authentication ── */}
            <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${twoFAEnabled ? "bg-green-100" : "bg-gray-100"}`}>
                    <Smartphone size={17} className={twoFAEnabled ? "text-green-600" : "text-gray-400"} />
                  </div>
                  <div>
                    <p className="font-bold text-gray-800 text-sm">Two-Factor Authentication (2FA)</p>
                    <p className={`text-xs mt-0.5 font-semibold ${twoFAEnabled ? "text-green-600" : "text-gray-400"}`}>
                      {twoFAEnabled ? "✅ Enabled — login requires authenticator code" : "⚠️ Disabled — anyone with credentials can log in"}
                    </p>
                  </div>
                </div>
                {twoFAEnabled && !twoFASetupMode && (
                  <button onClick={disableTwoFA} className="text-xs text-red-500 hover:text-red-700 border border-red-200 px-3 py-1.5 rounded-lg hover:bg-red-50 transition-all">
                    Disable
                  </button>
                )}
              </div>

              {twoFAError && <div className="bg-red-50 border border-red-200 text-red-600 text-xs rounded-xl px-3 py-2 mb-3">{twoFAError}</div>}
              {twoFASuccess && <div className="bg-green-50 border border-green-200 text-green-700 text-xs rounded-xl px-3 py-2 mb-3">{twoFASuccess}</div>}

              {!twoFAEnabled && !twoFASetupMode && (
                <button onClick={startTwoFASetup}
                  className="w-full py-2.5 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white rounded-xl font-semibold text-sm transition-all">
                  Setup 2FA with Google Authenticator
                </button>
              )}

              {twoFASetupMode && twoFATotp && (
                <div className="space-y-4">
                  <div className="bg-blue-50 border border-blue-100 rounded-xl p-3 text-xs text-blue-700">
                    <strong>Step 1:</strong> Install <strong>Google Authenticator</strong> or <strong>Authy</strong> on your phone.<br/>
                    <strong>Step 2:</strong> Scan the QR code below with the app.<br/>
                    <strong>Step 3:</strong> Enter the 6-digit code to confirm.
                  </div>

                  <div className="flex justify-center p-4 bg-white border border-gray-200 rounded-xl">
                    <QRCodeSVG value={twoFATotp.toString()} size={180} level="M" />
                  </div>

                  <details className="text-xs">
                    <summary className="text-gray-500 cursor-pointer hover:text-gray-700">Can't scan? Enter manually</summary>
                    <div className="mt-2 bg-gray-50 rounded-xl p-3 font-mono text-xs text-gray-700 break-all select-all">
                      {twoFATotp.secret.base32}
                    </div>
                    <p className="text-gray-400 mt-1">Account: CampusSwap Admin · Type: Time-based</p>
                  </details>

                  <form onSubmit={confirmTwoFASetup} className="space-y-3">
                    <div>
                      <label className="block text-xs text-gray-500 font-medium mb-1">Enter the 6-digit code to confirm</label>
                      <input
                        type="text" inputMode="numeric"
                        value={twoFAVerifyCode}
                        onChange={e => setTwoFAVerifyCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                        placeholder="_ _ _ _ _ _"
                        className="w-full border border-gray-200 rounded-xl py-3 px-4 text-center text-xl tracking-widest font-bold focus:outline-none focus:ring-2 focus:ring-green-400"
                        required
                      />
                    </div>
                    <div className="flex gap-2">
                      <button type="button" onClick={() => { setTwoFASetupMode(false); setTwoFATotp(null); setTwoFAError(""); }}
                        className="flex-1 py-2.5 border border-gray-200 text-gray-600 rounded-xl text-sm font-medium hover:bg-gray-50">
                        Cancel
                      </button>
                      <button type="submit" disabled={twoFAVerifyCode.length !== 6}
                        className="flex-1 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-xl font-semibold text-sm disabled:opacity-50 transition-all">
                        Enable 2FA
                      </button>
                    </div>
                  </form>
                </div>
              )}
            </div>

            {/* ── Change Firebase Account Password ── */}
            <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-4">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-9 h-9 bg-blue-100 rounded-xl flex items-center justify-center">
                  <Key size={17} className="text-blue-600" />
                </div>
                <div>
                  <p className="font-bold text-gray-800 text-sm">Change Login Password</p>
                  <p className="text-xs text-gray-400 mt-0.5">Your Firebase account password</p>
                </div>
              </div>
              {pwError && <div className="bg-red-50 border border-red-200 text-red-600 text-xs rounded-xl px-3 py-2 mb-3">{pwError}</div>}
              {pwSuccess && <div className="bg-green-50 border border-green-200 text-green-700 text-xs rounded-xl px-3 py-2 mb-3">{pwSuccess}</div>}
              <form onSubmit={handleChangeFirebasePassword} className="space-y-3">
                {[
                  { key: "current", label: "Current Password", placeholder: "Your current password" },
                  { key: "newPw",   label: "New Password",     placeholder: "Min 8 characters" },
                  { key: "confirm", label: "Confirm New Password", placeholder: "Repeat new password" },
                ].map(({ key, label, placeholder }) => (
                  <div key={key}>
                    <label className="block text-xs text-gray-500 font-medium mb-1">{label}</label>
                    <div className="relative">
                      <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input
                        type={showPw ? "text" : "password"}
                        value={pwForm[key]}
                        onChange={e => setPwForm(p => ({ ...p, [key]: e.target.value }))}
                        placeholder={placeholder}
                        required
                        className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                      />
                    </div>
                  </div>
                ))}
                <label className="flex items-center gap-2 text-xs text-gray-500 cursor-pointer">
                  <input type="checkbox" checked={showPw} onChange={() => setShowPw(p => !p)} className="rounded" />
                  Show passwords
                </label>
                <button type="submit" disabled={changingPw}
                  className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold text-sm disabled:opacity-50 transition-all">
                  {changingPw ? "Changing..." : "Change Login Password"}
                </button>
              </form>
            </div>

            {/* ── Change Admin Secret Password ── */}
            <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-4">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-9 h-9 bg-purple-100 rounded-xl flex items-center justify-center">
                  <ShieldCheck size={17} className="text-purple-600" />
                </div>
                <div>
                  <p className="font-bold text-gray-800 text-sm">Change Admin Secret</p>
                  <p className="text-xs text-gray-400 mt-0.5">The secret password required at login</p>
                </div>
              </div>
              {secretError && <div className="bg-red-50 border border-red-200 text-red-600 text-xs rounded-xl px-3 py-2 mb-3">{secretError}</div>}
              {secretSuccess && <div className="bg-green-50 border border-green-200 text-green-700 text-xs rounded-xl px-3 py-2 mb-3">{secretSuccess}</div>}
              <form onSubmit={handleChangeAdminSecret} className="space-y-3">
                {[
                  { key: "current",   label: "Current Secret",     placeholder: "Current admin secret" },
                  { key: "newSecret", label: "New Secret",          placeholder: "Min 8 characters" },
                  { key: "confirm",   label: "Confirm New Secret",  placeholder: "Repeat new secret" },
                ].map(({ key, label, placeholder }) => (
                  <div key={key}>
                    <label className="block text-xs text-gray-500 font-medium mb-1">{label}</label>
                    <div className="relative">
                      <ShieldCheck size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input
                        type="password"
                        value={secretForm[key]}
                        onChange={e => setSecretForm(p => ({ ...p, [key]: e.target.value }))}
                        placeholder={placeholder}
                        required
                        className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-purple-400"
                      />
                    </div>
                  </div>
                ))}
                <button type="submit" disabled={changingSecret}
                  className="w-full py-2.5 bg-purple-600 hover:bg-purple-700 text-white rounded-xl font-semibold text-sm disabled:opacity-50 transition-all">
                  {changingSecret ? "Updating..." : "Change Admin Secret"}
                </button>
              </form>
            </div>

          </div>
        )}

        {/* ── COMMUNITY POSTS ────────────────────────────────────────────── */}
        {tab === "community" && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                Community Posts ({communityPosts.length})
              </p>
              <button onClick={fetchCommunityPosts} className="text-xs text-cyan-600 flex items-center gap-1 hover:underline">
                <RefreshCw size={12} /> Refresh
              </button>
            </div>

            {/* Filter tabs */}
            <div className="flex gap-2 overflow-x-auto pb-1">
              {["all", "Notes", "Swap", "Lost & Found", "Rideshare", "Events", "Tutoring", "Giveaway"].map(f => (
                <button key={f} onClick={() => setCommunityFilter(f)}
                  className={`whitespace-nowrap px-3 py-1.5 rounded-full text-xs font-medium flex-shrink-0 transition
                    ${communityFilter === f ? "bg-cyan-600 text-white" : "bg-white text-gray-500 border border-gray-200"}`}>
                  {f === "all" ? `All (${communityPosts.length})` : `${f} (${communityPosts.filter(p => p._label === f).length})`}
                </button>
              ))}
            </div>

            {loadingCommunity ? (
              <Skeleton />
            ) : (() => {
              const filtered = communityFilter === "all" ? communityPosts : communityPosts.filter(p => p._label === communityFilter);
              if (filtered.length === 0) return (
                <div className="text-center py-12 bg-white rounded-2xl shadow-sm border border-gray-100">
                  <MessageSquare size={32} className="mx-auto mb-2 text-gray-200" />
                  <p className="text-gray-400 text-sm">No posts found</p>
                </div>
              );
              return filtered.map(post => {
                const title = post[post._titleField] || post.title || post.itemName || post.name || post.offeringTitle || "Untitled";
                const subtitle = post._label === "Rideshare"
                  ? `${post.from || ""} → ${post.to || ""}`
                  : post._label === "Tutoring"
                  ? (post.subjects?.join(", ") || post.bio || "")
                  : post._label === "Swap"
                  ? `Wants: ${post.wantingTitle || post.wantingCategory || ""}`
                  : post.description || post.subject || post.category || "";
                const LABEL_COLORS = {
                  "Notes":       "bg-yellow-100 text-yellow-700",
                  "Swap":        "bg-purple-100 text-purple-700",
                  "Lost & Found":"bg-orange-100 text-orange-700",
                  "Rideshare":   "bg-teal-100 text-teal-700",
                  "Events":      "bg-pink-100 text-pink-700",
                  "Tutoring":    "bg-indigo-100 text-indigo-700",
                  "Giveaway":    "bg-green-100 text-green-700",
                };
                return (
                  <div key={`${post._node}-${post.id}`} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
                    <div className="flex items-start gap-3">
                      {post.imageUrl ? (
                        <img src={post.imageUrl} alt={title} className="w-14 h-14 rounded-xl object-cover flex-shrink-0 border border-gray-100" />
                      ) : (
                        <div className="w-14 h-14 rounded-xl bg-gray-100 flex items-center justify-center flex-shrink-0">
                          <MessageSquare size={20} className="text-gray-300" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${LABEL_COLORS[post._label] || "bg-gray-100 text-gray-600"}`}>
                            {post._label}
                          </span>
                          {post.price !== undefined && (
                            <span className="text-xs font-bold text-gray-600">
                              {Number(post.price) === 0 ? "FREE" : `₹${post.price}`}
                            </span>
                          )}
                        </div>
                        <p className="font-semibold text-gray-800 text-sm mt-1 line-clamp-1">{title}</p>
                        {subtitle && <p className="text-xs text-gray-400 line-clamp-1 mt-0.5">{subtitle}</p>}
                        <p className="text-xs text-gray-400 mt-1">
                          By: {post.sellerName || post.posterName || post.name || "Unknown"}
                          {post.college && ` · ${post.college}`}
                        </p>
                        <p className="text-xs text-gray-300 mt-0.5">
                          {post.createdAt ? new Date(post.createdAt).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : ""}
                        </p>
                      </div>
                      <button
                        onClick={() => deleteCommunityPost(post._node, post.id)}
                        className="p-2 rounded-xl bg-red-50 hover:bg-red-100 text-red-500 flex-shrink-0"
                        title="Delete post"
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </div>
                );
              });
            })()}
          </div>
        )}

        {/* ── Feedback Tab ─────────────────────────────────────────────────── */}
        {tab === "feedback" && (
          <div className="space-y-3">
            <div className="flex items-center justify-between mb-1">
              <p className="text-sm font-bold text-gray-700">User Feedback & Complaints</p>
              <div className="flex gap-1.5">
                {["open", "resolved", "all"].map(f => (
                  <button key={f} onClick={() => setFeedbackFilter(f)}
                    className={`text-xs px-3 py-1.5 rounded-lg font-semibold capitalize border transition ${feedbackFilter === f ? "bg-violet-600 text-white border-violet-600" : "bg-white text-gray-500 border-gray-200 hover:border-gray-300"}`}>
                    {f}
                  </button>
                ))}
                <button onClick={fetchFeedback} className="text-xs text-violet-600 flex items-center gap-1 hover:underline ml-1">
                  <RefreshCw size={11} /> Refresh
                </button>
              </div>
            </div>
            {feedbackItems.filter(f => feedbackFilter === "all" || f.status === feedbackFilter).length === 0 ? (
              <div className="bg-white rounded-2xl p-8 text-center shadow-sm">
                <p className="text-gray-400 text-sm">No {feedbackFilter === "all" ? "" : feedbackFilter} feedback yet.</p>
              </div>
            ) : (
              feedbackItems
                .filter(f => feedbackFilter === "all" || f.status === feedbackFilter)
                .map(f => (
                  <div key={f.id} className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className={`text-xs px-2 py-0.5 rounded-full font-semibold border ${
                            f.type === "Bug Report" ? "bg-red-50 text-red-700 border-red-200" :
                            f.type === "Complaint" ? "bg-orange-50 text-orange-700 border-orange-200" :
                            f.type === "Suggestion" ? "bg-blue-50 text-blue-700 border-blue-200" :
                            f.type === "College Change Request" ? "bg-purple-50 text-purple-700 border-purple-200" :
                            "bg-gray-50 text-gray-600 border-gray-200"
                          }`}>{f.type}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${f.status === "open" ? "bg-amber-100 text-amber-700" : "bg-green-100 text-green-700"}`}>
                            {f.status}
                          </span>
                          <span className="text-xs text-gray-400">{f.userName} · {f.college || "—"}</span>
                          {f.userEmail && <span className="text-xs text-gray-400">{f.userEmail}</span>}
                        </div>
                        <p className="text-sm text-gray-700 whitespace-pre-wrap">{f.message}</p>
                        <p className="text-xs text-gray-400 mt-1">{f.createdAt ? new Date(f.createdAt).toLocaleString("en-IN") : "—"}</p>
                      </div>
                      <div className="flex gap-2 flex-shrink-0 flex-col items-end">
                        {f.status === "open" && f.type === "College Change Request" && (
                          <button onClick={() => approveCollegeChange(f.uid, f.id)}
                            className="text-xs bg-purple-50 text-purple-700 border border-purple-200 px-3 py-1.5 rounded-xl hover:bg-purple-100 font-semibold whitespace-nowrap">
                            ✓ Approve College Change
                          </button>
                        )}
                        {f.status === "open" && f.type !== "College Change Request" && (
                          <button onClick={() => markFeedbackResolved(f.uid, f.id, f.type)}
                            className="text-xs bg-green-50 text-green-700 border border-green-200 px-3 py-1.5 rounded-xl hover:bg-green-100 font-semibold">
                            ✓ Resolve
                          </button>
                        )}
                        {f.status === "open" && f.type === "College Change Request" && (
                          <button onClick={() => markFeedbackResolved(f.uid, f.id, f.type)}
                            className="text-xs bg-gray-50 text-gray-600 border border-gray-200 px-3 py-1.5 rounded-xl hover:bg-gray-100 font-semibold whitespace-nowrap">
                            ✗ Reject
                          </button>
                        )}
                        <button onClick={() => deleteFeedback(f.uid, f.id)}
                          className="text-xs bg-red-50 text-red-600 border border-red-200 px-3 py-1.5 rounded-xl hover:bg-red-100 font-semibold">
                          🗑 Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))
            )}
          </div>
        )}

      </div>
    </div>
  );
}
